import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { replayRoundToHistory, type ReplayResponse } from '../api/types';
import { useApp } from '../app/AppContext';
import { ACTION_GLYPH, actionLabelKey } from '../components/ChoiceButtons';
import { FairnessPanel } from '../components/FairnessPanel';
import { HistoryTable } from '../components/HistoryTable';
import { useI18n } from '../i18n';
import { outcomeFor } from '../match/machine';

/**
 * 回放檢視器（C02 §5 `GET /matches/{id}/replay`）。
 *
 * 回放是「已結束對局的公開資料」，所以這裡揭露雙方動作沒有資訊邊界問題。
 * 它同時是可驗證公平的載體：回放包含 server_seed 原文與 transcript_hash，
 * 玩家可以在這頁按下驗證按鈕，用自己的瀏覽器重算一次。
 */
export function ReplayPage() {
  const { matchId = '' } = useParams();
  const { api, session } = useApp();
  const { t } = useI18n();
  const [replay, setReplay] = useState<ReplayResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!matchId) return;
    const ac = new AbortController();
    api
      .getReplay(matchId, ac.signal)
      .then(setReplay)
      .catch(() => setError(t('error.notFound')));
    return () => ac.abort();
  }, [api, matchId, t]);

  const rounds = useMemo(() => replay?.rounds ?? [], [replay]);
  /*
   * ★ `/replay` 的 round **沒有 `winner` 欄位** —— 勝方在 `detail.winner`
   *   （後端 `public_rounds()`）。舊版直接把 `rounds` 當成 `HistoryEntry[]` 用，
   *   `round.winner` 永遠是 `undefined`，`outcomeFor(seat, undefined)` 一律回 `'lose'`：
   *   **整份回放的每一回合都被畫成「敗」**。型別過了，畫面在說謊。
   *   顯示用的形狀在這裡轉一次；驗證用的仍然是原文（見 <FairnessPanel>）。
   */
  const shownHistory = useMemo(() => rounds.map(replayRoundToHistory), [rounds]);

  useEffect(() => {
    if (!playing) return;
    timerRef.current = setInterval(() => {
      setCursor((c) => {
        if (c >= rounds.length) {
          setPlaying(false);
          return c;
        }
        return c + 1;
      });
    }, 1100);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [playing, rounds.length]);

  const mySeat = useMemo(() => {
    if (!replay || !session) return null;
    // 回放是公開的，可能不是我打的那場；找不到就以 seat 0 的視角呈現。
    const mine = replay.seats.find((s) => s.display_name === session.displayName);
    return mine?.seat ?? replay.seats[0]?.seat ?? null;
  }, [replay, session]);

  const oppSeat = useMemo(() => replay?.seats.find((s) => s.seat !== mySeat)?.seat ?? null, [replay, mySeat]);

  const step = useCallback(() => setCursor((c) => Math.min(rounds.length, c + 1)), [rounds.length]);
  const restart = useCallback(() => {
    setCursor(0);
    setPlaying(false);
  }, []);

  if (error) {
    return (
      <div className="notice notice--error stack stack--tight">
        <strong>{error}</strong>
        <Link to="/">{t('match.backToLobby')}</Link>
      </div>
    );
  }
  if (!replay) return <p className="muted">{t('replay.loading')}</p>;

  const shown = shownHistory.slice(0, cursor);
  const current = cursor > 0 ? shownHistory[cursor - 1] : null;

  return (
    <div className="stack">
      <div className="spread">
        <h1 className="h1">{t('replay.title')}</h1>
        <Link className="btn btn--ghost btn--sm" to="/">
          {t('match.backToLobby')}
        </Link>
      </div>

      <section className="card stack">
        <div className="row">
          <button type="button" className="btn btn--sm" onClick={() => setPlaying((p) => !p)}>
            {playing ? t('replay.pause') : t('replay.play')}
          </button>
          <button type="button" className="btn btn--sm" onClick={step} disabled={cursor >= rounds.length}>
            {t('replay.step')}
          </button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={restart}>
            {t('replay.restart')}
          </button>
          <span className="faint">
            {cursor} / {rounds.length}
          </span>
        </div>

        {current ? (
          <div className="reveal">
            <div className="reveal__hand">
              <span className="reveal__glyph" aria-hidden="true">
                {ACTION_GLYPH[current.actions[String(mySeat)] ?? ''] ?? '?'}
              </span>
              <strong>
                {current.actions[String(mySeat)] ? t(actionLabelKey(current.actions[String(mySeat)]!)) : '—'}
              </strong>
            </div>
            <p className={`reveal__verdict reveal__verdict--${outcomeFor(mySeat, current.winner)}`}>
              {t('match.round', { n: current.round_no })}
            </p>
            <div className="reveal__hand reveal__hand--theirs">
              <span className="reveal__glyph" aria-hidden="true">
                {ACTION_GLYPH[current.actions[String(oppSeat)] ?? ''] ?? '?'}
              </span>
              <strong>
                {current.actions[String(oppSeat)] ? t(actionLabelKey(current.actions[String(oppSeat)]!)) : '—'}
              </strong>
            </div>
          </div>
        ) : null}

        <HistoryTable history={shown} mySeat={mySeat} opponentSeatNo={oppSeat} />
      </section>

      <FairnessPanel
        matchId={replay.match_id}
        fairness={{
          server_seed_commit: replay.server_seed_commit,
          server_seed: replay.server_seed,
          transcript_hash: replay.transcript_hash,
        }}
        rounds={replay.rounds}
        ended
        transcriptEncoding={replay.transcript_encoding}
      />
    </div>
  );
}
